// Storefront-spezifische Domain-TLDs für die Endkunden-Frontends.
// Quelle: Kaufland Seller-Portal, die TLD entspricht direkt dem storefront-Code.
const KAUFLAND_TLD: Record<string, string> = {
  de: 'de',
  at: 'at',
  cz: 'cz',
  sk: 'sk',
  pl: 'pl',
};

/**
 * Endkunden-Produktseite (Marketplace-Frontend) für ein Kaufland-Listing.
 * URL-Schema: `https://www.kaufland.{tld}/product/{id_product}/`.
 *
 * Voraussetzung: `id_product` muss am Unit hinterlegt sein — Kaufland setzt das
 * normalerweise nach Bestätigung der Produktzuordnung; bis dahin liefern wir null
 * und das UI zeigt eine leere Zelle.
 */
export function kauflandFrontendUrl(storefront: string, idProduct: number | string | null): string | null {
  if (!storefront || idProduct === null || idProduct === undefined || idProduct === '') return null;
  const tld = KAUFLAND_TLD[storefront.toLowerCase()];
  if (!tld) return null;
  return `https://www.kaufland.${tld}/product/${idProduct}/`;
}

/**
 * Seller-Portal-Deeplink für die Listing-Bearbeitung. Das Seller-Portal liegt
 * domain-stabil unter `sellerportal.kaufland.de` (verwendet TLD `.de` auch für
 * andere Storefronts) — gewünschtes Land im Pfad als letztes Segment.
 *
 * Format: `https://sellerportal.kaufland.de/offers/{idUnit}/{storefront}`
 * (`idOffer` bleibt im Funktions-Argument für Backwards-Compat, wird aber für die
 * URL nicht mehr gebraucht — der Deeplink verlangt explizit die `idUnit`).
 */
export function kauflandBackendUrl(
  storefront: string,
  idUnit: string | null,
  _idOffer: string | null,
): string | null {
  if (!storefront || !idUnit) return null;
  return `https://sellerportal.kaufland.de/offers/${encodeURIComponent(idUnit)}/${storefront.toLowerCase()}`;
}

/**
 * Seller-Portal-Deeplink für eine Bestellung. URL-Format (per User 2026-05-19):
 *   `https://sellerportal.kaufland.de/order/{id_order}`
 * Storefront wird nicht im Pfad referenziert — das Seller-Portal löst die Bestellung
 * über die globale `id_order` auf (Kaufland-Bestell-IDs sind cross-storefront eindeutig).
 */
export function kauflandBackendOrderUrl(idOrder: string | null): string | null {
  if (!idOrder) return null;
  return `https://sellerportal.kaufland.de/order/${encodeURIComponent(idOrder)}`;
}

/**
 * Vario-Article-Detail-Deeplink. Format (vom User vorgegeben):
 *   `https://<vario-host>/products/articles/{articleId}/dashboard`
 *
 * Der Vario-Host (= Tenant-spezifische Subdomain wie `goecommerce.vario.cloud`) wird zur
 * Laufzeit aus dem Browser-Kontext bestimmt:
 *   1) `window.location.ancestorOrigins[0]` — wenn wir in der Vario-iframe laufen
 *   2) `document.referrer` — Fallback wenn ancestorOrigins nicht unterstützt
 *   3) wenn keins greift → null (Icon wird dann disabled gerendert)
 */
export function varioArticleUrl(articleId: string | null): string | null {
  if (!articleId) return null;
  const base = detectVarioBaseUrl();
  if (!base) return null;
  return `${base}/products/articles/${encodeURIComponent(articleId)}/dashboard`;
}

/**
 * Vario-Verkaufsauftrag-Deeplink. Format (vom User 2026-05-18 verifiziert):
 *   `https://<vario-host>/documents-sales/{varioOrderId}`
 * `/sales/orders/{id}/dashboard` lieferte 404 — Vario routet Aufträge unter
 * `documents-sales` (Belegart-Group). Host-Detection siehe detectVarioBaseUrl();
 * null = nicht im Vario-iframe → Link wird nicht klickbar gerendert.
 */
export function varioSalesOrderUrl(varioOrderId: string | null): string | null {
  if (!varioOrderId) return null;
  const base = detectVarioBaseUrl();
  if (!base) return null;
  return `${base}/documents-sales/${encodeURIComponent(varioOrderId)}`;
}

function detectVarioBaseUrl(): string | null {
  // 1) ancestorOrigins (Chrome/Safari, nicht Firefox)
  const ancestors = (window.location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins;
  if (ancestors && ancestors.length > 0) {
    const top = ancestors[ancestors.length - 1];
    if (top && /\.vario\.cloud$/i.test(new URL(top).hostname)) return top.replace(/\/+$/, '');
  }
  // 2) document.referrer (alle Browser, fällt aber bei nachträglicher Navigation aus)
  if (document.referrer) {
    try {
      const refUrl = new URL(document.referrer);
      if (/\.vario\.cloud$/i.test(refUrl.hostname)) return `${refUrl.protocol}//${refUrl.host}`;
    } catch {
      /* ignore */
    }
  }
  return null;
}

/**
 * Länderflagge als Unicode-Regional-Indicator-Symbole.
 * 'de' → '🇩🇪', 'fr' → '🇫🇷'. Liefert das Original zurück, wenn der Code keine
 * zwei Buchstaben hat.
 */
export function countryFlag(storefront: string): string {
  if (!storefront || storefront.length !== 2) return '';
  const base = 0x1f1e6 - 'a'.charCodeAt(0);
  const code = storefront.toLowerCase();
  return String.fromCodePoint(base + code.charCodeAt(0), base + code.charCodeAt(1));
}
